// src/components/RequireAuth.tsx
import React, { JSX } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useUser } from '../contexts/UserContext'

interface RequireAuthProps {
  children: JSX.Element
  requireAdmin?: boolean
  requireSuperAdmin?: boolean
  // True when the route entry in routesConfig has externalUserAllowed: true.
  // External API users (is_external_api_user) can only access these routes.
  externalUserAllowed?: boolean
}

// Guard flags are driven solely by each route's `requireAdmin` / `requireSuperAdmin`
// / `externalUserAllowed` entries in routesConfig.tsx. App.tsx wires those flags
// into this component on every authenticated route, so adding a new admin-only
// or candidate-flow route only requires setting the flag in the config.
const RequireAuth: React.FC<RequireAuthProps> = ({
  children,
  requireAdmin = false,
  requireSuperAdmin = false,
  externalUserAllowed = false,
}) => {
  const token = localStorage.getItem('testinvest_token')
  const location = useLocation()
  const { user, isAdmin, isSuperAdmin, isExternalApiUser } = useUser()

  // If no token, redirect to /login, preserving where they tried to go
  if (!token) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  // B2B external API users are confined to the test-taking flow. Anything not
  // explicitly marked externalUserAllowed bounces them back to /begin-test
  // (their landing page from the magic link).
  if (user && isExternalApiUser() && !externalUserAllowed) {
    return <Navigate to="/begin-test" replace />
  }

  if (user && requireSuperAdmin && !isSuperAdmin()) {
    return <Navigate to="/test-dashboard" replace />
  }

  if (user && requireAdmin && !isAdmin()) {
    return <Navigate to="/test-dashboard" replace />
  }

  return children
}

export default RequireAuth